// Which values each hose condition check offers, and which of them mean the
// hose needs attention.
//
// Every check used to offer the same three values (OK / not OK / new). The
// client asked for findings specific to the check instead of a generic fault,
// so the fire cock can report "dripping" and the water supply "rusty" or
// "sandy" (client #97).
//
// On those two checks the findings REPLACE "not OK" — the client confirmed
// the removal and will re-rate the affected hoses at the next maintenance.
// Until then a hose that already holds "not OK" still shows it, because
// conditionOptions always offers the stored value.

import { ConditionValue } from '@/data/types';
import type { HoseCondition } from '@/data/types';
import type { I18nKey } from '@/lib/i18n';

export const COND_KEYS: (keyof HoseCondition)[] = [
  'fireCock',
  'seals',
  'hose',
  'nozzle',
  'axle',
  'swivelArm',
  'doorLock',
  'water',
];

const BASE = [
  ConditionValue.OK,
  ConditionValue.NOT_OK,
  ConditionValue.NEW,
] as const;

// Checks whose specific findings stand in for the generic "not OK".
const FINDINGS: Partial<Record<keyof HoseCondition, ConditionValue[]>> = {
  fireCock: [ConditionValue.DRIPPING],
  water: [ConditionValue.RUSTY, ConditionValue.SANDY],
};

/**
 * Values offered for one check, in display order: OK first, then the faults,
 * then "new". A value already stored on the record is always included, so a
 * hose never shows a blank select because its value was retired.
 */
export function conditionOptions(
  key: keyof HoseCondition | null,
  current?: ConditionValue
): ConditionValue[] {
  // `null` is the extinguisher's overall condition, which is not one of the
  // hose checks and keeps the base three values.
  const faults = (key && FINDINGS[key]) || [ConditionValue.NOT_OK];
  const out = [ConditionValue.OK, ...faults, ConditionValue.NEW];
  return current && !out.includes(current) ? [...out, current] : out;
}

export const CONDITION_LABEL_KEYS = {
  [ConditionValue.OK]: 'hose.ok',
  [ConditionValue.NOT_OK]: 'hose.notOk',
  [ConditionValue.NEW]: 'common.new',
  [ConditionValue.DRIPPING]: 'cond.dripping',
  [ConditionValue.RUSTY]: 'cond.rusty',
  [ConditionValue.SANDY]: 'cond.sandy',
} as const satisfies Record<ConditionValue, I18nKey>;

const FAULTS: ConditionValue[] = [
  ConditionValue.NOT_OK,
  ConditionValue.DRIPPING,
  ConditionValue.RUSTY,
  ConditionValue.SANDY,
];

/**
 * Whether a value reports a problem. Drives the per-check note field and the
 * photo-documentation prompt, which previously keyed on NOT_OK alone — a
 * dripping fire cock is a defect and has to trigger the same evidence.
 */
export const isConditionFault = (v?: ConditionValue): boolean =>
  v != null && FAULTS.includes(v);

export const hasConditionFault = (c?: Partial<HoseCondition>): boolean =>
  !!c && COND_KEYS.some(k => isConditionFault(c[k]));

type ConditionNotes = Partial<Record<keyof HoseCondition, string>>;

/**
 * The defect notes worth saving: one per check that still reports a fault.
 * Flipping a check back to OK or new drops its now-stale note.
 */
export function faultNotes(
  condition: Partial<HoseCondition>,
  notes: ConditionNotes
): ConditionNotes {
  return Object.fromEntries(
    COND_KEYS.filter(k => isConditionFault(condition[k]) && notes[k]).map(k => [
      k,
      notes[k],
    ])
  );
}

export { BASE as BASE_CONDITION_VALUES };
